import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatFormField } from '@angular/material/form-field';
import { MatSliderChange } from '@angular/material/slider';
import { MatTooltip } from '@angular/material/tooltip';
import { ComponentDefinition } from '@formbird/types';
import { ChangedDocumentService } from 'formbird-services';
import { FormbirdInjectorService } from '@services/injector/formbird-injector.service';
import { ComponentSetupService } from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import { AbstractComponent } from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import { sclHandlebars } from '../../../../formbird-sc-shared/src/lib/handlebars/sclHandlebars';

interface SliderComponentDefinition extends ComponentDefinition {
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  valueLabel?: string;
  handlebarsHelpers?: string;
}

@Component({
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SliderComponent extends AbstractComponent<SliderComponentDefinition> implements AfterViewInit, OnInit {

  visible = true;
  disableSave = false;

  //
  min = 0;
  max = 100;
  step = 1;
  precision = 0;
  valueLabel = '';
  sliderLabel: any;

  isSliding = false;

  constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    protected cdRef: ChangeDetectorRef,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdRef);
  }

  ngOnInit() {

    this.shouldNotifyCompInitializedInSuper = false;

    if (!(typeof this.componentDefinition.visible === 'undefined' || this.componentDefinition.visible === null)) {
      this.visible = this.componentDefinition.visible;
    }

    if (!(typeof this.componentDefinition.disableSave === 'undefined' || this.componentDefinition.disableSave === null)) {
      this.disableSave = this.componentDefinition.disableSave;
    }

    if (!(typeof this.componentDefinition.min === 'undefined' || this.componentDefinition.min === null
      || isNaN(this.componentDefinition.min))) {
      this.min = this.componentDefinition.min;
    }

    if (!(typeof this.componentDefinition.max === 'undefined' || this.componentDefinition.max === null
      || isNaN(this.componentDefinition.max))) {
      this.max = this.componentDefinition.max;
    }

    if (!(typeof this.componentDefinition.step === 'undefined' || this.componentDefinition.step === null
      || isNaN(this.componentDefinition.step))) {
      this.step = this.componentDefinition.step;
    }

    if (!(typeof this.componentDefinition.precision === 'undefined' || this.componentDefinition.precision === null
      || isNaN(this.componentDefinition.precision))) {
      this.precision = this.componentDefinition.precision;
    }

    super.ngOnInit();

    this.setupLabel(parseFloat(this.fieldValue));

    const docSub = this.changedDocumentService.watchDocument(this.document.documentId).subscribe(doc => {

      this.document = doc;

    });

    const tplSub = this.changedDocumentService.watchDocument(this.template.documentId).subscribe(tpl => {
      this.template = tpl;

      this.setupLabel(this.fieldValue);

      this.detectChanges();
    });

    this.subs.add(docSub);
    this.subs.add(tplSub);

  }// end ngOnInit

  initializeFieldValue() {
    if (!(typeof this.componentDefinition.valueLabel === 'undefined' || this.componentDefinition.valueLabel === null)) {
      this.valueLabel = this.componentDefinition.valueLabel;
    }

    const formatNumber = this.formattedNumber(this.fieldValue);
    this.fieldValue = parseFloat(formatNumber);

  }

  onFieldValueChanged(value) {
    if (this.isSliding) {
      return;
    }

    this.setupLabel(value);

    if (this.fieldValue !== value) {
      this.fieldValue = value;
      this.formControl.patchValue(this.fieldValue, {emitEvent: false, onlySelf: true});
    }

    this.detectChanges();
  }

  ngAfterViewInit() {
    sclHandlebars.initHandlebars(this.componentDefinition.handlebarsHelpers);
  }

  formattedNumber(valNumber) {
    if (this.precision > 0) {
      return parseFloat(valNumber).toFixed(this.precision);
    } else {
      return valNumber;
    }
  }

  processHandlebarsWithContext(valueLabel) {
    const handlebarsContext = {
      document: this.document,
      doc: this.document,
      tpl: this.template,
      template: this.template,
      componentDefinition: this.componentDefinition // the current components configuration details
    };
    return sclHandlebars.processHandlebars(valueLabel, null, handlebarsContext);
  }

  setupLabel(val) {
    if (isNaN(val)) {
      val = 0;
    }
    let result = this.formattedNumber(val);
    if (this.valueLabel) {
      result = this.processHandlebarsWithContext(this.valueLabel);
    }
    this.sliderLabel = result;
  }

  onSliderInputChange(event: MatSliderChange, fieldValue?) {
    this.isSliding = false;
    let val = 0;
    if (event) {
      this.isSliding = true;
      val = event.value;
    }
    if (!(typeof fieldValue === 'undefined' || fieldValue === null)) {
      val = fieldValue;
    }
    if (this.valueLabel) {
      this.sliderLabel = this.processHandlebarsWithContext(
        this.valueLabel.split('{{document.' + this.fieldName + '}}').join(this.formattedNumber(val) + '')
      );
    } else {
      this.setupLabel(parseFloat(this.formattedNumber(val)));
    }

  }

  onValueChanged(value) {
    this.fieldValue = value;

    super.valueChanged(value);

    this.isSliding = false;

  }

  onControlValueChanged(fieldValue) {
    console.log('onControlValueChanged', fieldValue);
    this.onSliderInputChange(undefined, fieldValue);
    this.onFieldValueChanged(fieldValue);
  }

  showToolTip(tooltip: MatTooltip, formFieldContainer: MatFormField, labelContainer: HTMLElement) {
    return (labelContainer.offsetWidth <= formFieldContainer._elementRef.nativeElement.scrollWidth);
  }

  toggleTooltip(tooltip: MatTooltip) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }
}
